import { useState, useRef, useEffect } from "react";
import { ChevronDown, ExternalLink } from "lucide-react";
import { TIMELINE, GEO_COLOR } from "./homeData";

export function JourneySection() {
  const [geoFilter, setGeoFilter] = useState("all");
  const [showFull, setShowFull] = useState(false);
  const [expandedTL, setExpandedTL] = useState({});
  const timelineRef = useRef(null);
  const sectionRef = useRef(null);

  /* Local IntersectionObserver for reveal animations within this section */
  useEffect(() => {
    const root = sectionRef.current;
    if (!root) return;
    const els = root.querySelectorAll(".reveal:not(.visible), .scale-reveal:not(.visible)");
    if (els.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("visible"); obs.unobserve(e.target); }
      }),
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [showFull, geoFilter]);

  useEffect(() => {
    const tl = timelineRef.current;
    if (!tl) return;
    const onScroll = () => {
      const rect = tl.getBoundingClientRect();
      const vh = window.innerHeight;
      if (rect.top > vh || rect.bottom < 0) {
        tl.style.setProperty("--line-height", "0%");
        return;
      }
      const progress = Math.min(1, Math.max(0, (vh - rect.top) / (rect.height + vh * 0.3)));
      tl.style.setProperty("--line-height", `${(progress * 100).toFixed(1)}%`);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [showFull, geoFilter]);

  const filteredTL = TIMELINE.filter((t) => {
    if (geoFilter !== "all" && !t.geo.includes(geoFilter)) return false;
    if (!showFull && !t.isDefault) return false;
    return true;
  });

  return (
    <section ref={sectionRef} id="journey" className="hm-section py-16 md:py-24" style={{ background: "var(--sky)" }} data-testid="journey-section">
      <div className="max-w-6xl mx-auto px-6">
        <p className="hm-section-label reveal" data-testid="journey-label">The journey</p>
        <h2 className="hm-display reveal reveal-d1" style={{ fontSize: "clamp(1.5rem, 3.5vw, 1.8rem)", fontWeight: 600, color: "var(--ink)", marginTop: 8 }} data-testid="journey-heading">Two decades of building</h2>
        <p className="hm-display reveal reveal-d2" style={{ fontStyle: "italic", color: "var(--amber)", fontSize: "1.05rem", marginTop: 4 }}>Where Dreams Take Shape!</p>
        <p className="reveal reveal-d3" style={{ color: "var(--ink2)", lineHeight: 1.75, marginTop: 16, maxWidth: 680 }} data-testid="journey-intro">From village farms to global platforms — every step has been about connecting people and creating possibilities. Collaborated, co-founded and co-initiated with amazing teams, friendships and multi-sector partnerships across systems.</p>

        <div className="flex flex-wrap gap-2 mt-8 reveal" data-testid="journey-filters">
          {[["all", "All"], ["nepal", "Nepal"], ["switzerland", "Switzerland"], ["global", "Global"]].map(([val, label]) => (
            <button key={val} className={`hm-filter ${geoFilter === val ? "active" : ""}`} onClick={() => { setGeoFilter(val); setShowFull(val !== "all" ? true : showFull); }} data-testid={`filter-${val}`}>{label}</button>
          ))}
        </div>

        <div ref={timelineRef} className="hm-timeline-container mt-8 space-y-3" data-testid="journey-timeline">
          {filteredTL.map((t) => {
            const borderColor = GEO_COLOR[t.geo[0]] || "var(--div)";
            const key = `${t.year}-${t.org}`;
            return (
              <div key={key} className="hm-tl-item scale-reveal" style={{ borderLeftColor: borderColor, transitionDelay: `${filteredTL.indexOf(t) * 80}ms` }} onClick={() => setExpandedTL((prev) => ({ ...prev, [key]: !prev[key] }))} data-testid={`tl-${key}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <span className="hm-mono" style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", color: borderColor }}>{t.year}</span>
                    <p className="hm-display mt-1" style={{ fontSize: "1.05rem", fontWeight: 600, color: "var(--ink)" }}>{t.role}</p>
                    <p className="hm-body" style={{ fontSize: "0.85rem", fontStyle: "italic", color: "var(--ink3)", marginTop: 2 }}>{t.org} &middot; {t.location}</p>
                  </div>
                  <ChevronDown size={16} className={`hm-chevron ${expandedTL[key] ? "open" : ""}`} style={{ color: "var(--ink3)", marginTop: 8 }} />
                </div>
                <div className={`hm-expand ${expandedTL[key] ? "open" : ""}`}>
                  <p className="hm-body mt-3" style={{ fontSize: "0.9rem", lineHeight: 1.75, color: "var(--ink2)" }}>{t.detail}</p>
                  {t.links.length > 0 && (
                    <div className="flex flex-wrap gap-3 mt-2">
                      {t.links.map((lk) => (
                        <a key={lk.url} href={lk.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1" style={{ color: "var(--blue)", fontSize: "0.8rem", textDecoration: "none" }} onClick={(e) => e.stopPropagation()}>
                          <ExternalLink size={12} /> {lk.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {geoFilter === "all" && (
          <button onClick={() => setShowFull(!showFull)} className="reveal mt-6" style={{ color: "var(--blue)", background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "0.95rem" }} data-testid="journey-toggle">
            {showFull ? "Show recent only \u2191" : "Show full journey \u2193"}
          </button>
        )}
      </div>
    </section>
  );
}
